import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Cog6ToothIcon,
  GlobeAltIcon,
  CurrencyRupeeIcon,
  PaintBrushIcon,
  ChatBubbleLeftRightIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { BackButton, Button, Input, Card, LoadingSpinner, SearchableSelect } from '@/components/common';
import { AssetPickerField } from '@/components/media';
import { useAssetConfig } from '@/hooks/useAssets';
import type { MediaAsset } from '@/services/assets';
import { brokerService, getErrorMessage } from '@/services';
import { useAuthStore } from '@/store';
import toast from 'react-hot-toast';
import { settlementPolicyOptions } from './settlementPolicy';
import { MessagingChannelsSection } from './MessagingChannelsSection';
import { BrokerAssistantKeysSection } from './BrokerAssistantKeysSection';
import { BrokerKycSection } from './BrokerKycSection';
import { canSeeKycTab } from './brokerKycForm';

interface BrokerSettings {
  custom_domain: string;
  logo_url: string;
  primary_color: string;
  secondary_color: string;
  show_powered_by: boolean;
  default_tds_percentage: string;
  default_gst_percentage: string;
  payout_frequency: string;
  settlement_policy: string;
  settings: Record<string, unknown>;
  features: Record<string, unknown>;
  /** V-01(a): duplicate contacts are ALLOWED unless this broker turns the
   *  check back on. Stored server-side in `Broker.settings`, but sent and
   *  returned as its own boolean so saving it cannot clobber the rest of
   *  that blob. */
  allow_duplicate_contacts: boolean;
  /** When the public quote page shows the insurer Pay button. */
  online_payment_gate?: 'immediate' | 'after_approval';
  /** Public motor quotes: price first and let the customer adjust, or ask
   *  claim / NCB / add-ons / IDV before the first price. */
  public_quote_customise?: 'quote_first' | 'ask_first';
  /** Rupees of agent payout per XP point (Broker.settings, merged server-side). Default "10.00". */
  posp_xp_ratio?: string;
}

/**
 * The "Broker" tab is gone (item 1b).
 *
 * It edited the broker's identity and licensing — name, username, licence, PAN,
 * GST, CIN — which the Broker Details page now owns as ONE editable form
 * alongside the contact details. Two editors for one record is how the two
 * surfaces disagreed in the first place; this page keeps the white-label and
 * commission settings, which live on a different serializer
 * (`BrokerSettingsSerializer`) and have no other home.
 *
 * The tab's state went with it: `organization` was also the DEFAULT tab, so
 * removing it from this list alone would have left the page rendering nothing
 * until a tab was clicked. `General` is the default now.
 */
const TABS = [
  { id: 'general', label: 'General', icon: Cog6ToothIcon },
  { id: 'branding', label: 'Branding', icon: PaintBrushIcon },
  { id: 'domain', label: 'Domain', icon: GlobeAltIcon },
  { id: 'commission', label: 'Commission', icon: CurrencyRupeeIcon },
  // The broker's own bots. Only on the broker admin's own page (/broker/settings): the
  // messaging API answers broker admins only, and brokers own their bots.
  { id: 'messaging', label: 'Messaging channels', icon: ChatBubbleLeftRightIcon },
  // Messaging Plan 4: every AI assistant key at this broker. Broker admin's own page only (the API answers
  // broker admins only).
  { id: 'assistants', label: 'AI assistants', icon: CpuChipIcon },
  // #331: the broker admin's own compliance record (the API answers broker admins only).
  { id: 'kyc', label: 'Compliance / KYC', icon: Cog6ToothIcon },
] as const;

type TabId = (typeof TABS)[number]['id'];

export default function BrokerSettingsPage() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const { id: brokerIdParam } = useParams<{ id: string }>();
  const brokerId = brokerIdParam || user?.tenant_id;
  const visibleTabs = TABS.filter((tab) =>
    tab.id === 'kyc'
      ? canSeeKycTab(user?.role, !!brokerIdParam)
      : (tab.id !== 'messaging' && tab.id !== 'assistants') || !brokerIdParam
  );
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [form, setForm] = useState<Partial<BrokerSettings>>({});
  const [isDirty, setIsDirty] = useState(false);
  // The asset just chosen in this session. `form.logo_url` is what is SAVED;
  // this is only so the preview can show the picked image before a save, and
  // so clearing the field clears the preview too.
  const [pickedLogo, setPickedLogo] = useState<MediaAsset | null>(null);
  const { data: assetConfig, isError: assetConfigError } = useAssetConfig();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['brokerSettings', brokerId],
    queryFn: () => brokerService.getBrokerSettings(brokerId!),
    enabled: !!brokerId,
  });

  useEffect(() => {
    if (settings) {
      setForm(settings);
      setIsDirty(false);
    }
  }, [settings]);

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      brokerService.updateBrokerSettings(brokerId!, data),
    onSuccess: (saved: Record<string, unknown>, sent) => {
      queryClient.invalidateQueries({ queryKey: ['brokerSettings', brokerId] });
      // The PATCH response IS the stored record (DRF re-serializes the saved
      // instance), so the success message can be checked against it instead of
      // asserted. A logo that silently failed to store used to be invisible
      // until the next page load.
      const sentLogo = (sent.logo_url ?? '') as string;
      const storedLogo = (saved?.logo_url ?? '') as string;
      if (sentLogo !== storedLogo) {
        toast.error('Saved, but the logo did not store. Reload and try again.');
      } else {
        toast.success('Settings saved');
      }
      setIsDirty(false);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const updateField = (field: string, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleSave = () => {
    // `settings` and `features` are free-form JSON blobs this page does not
    // edit — it only ever echoed back whatever the GET returned. Sending them
    // is a read-modify-write: a country admin blocking the broker writes
    // `status_reason` into `settings`, and a save from a form loaded before
    // that would put the old blob back. The one key this page does own,
    // `allow_duplicate_contacts`, travels as its own boolean and is merged
    // server-side, so there is nothing left to echo.
    const payload: Partial<BrokerSettings> = { ...form };
    delete payload.settings;
    delete payload.features;
    mutation.mutate(payload as Record<string, unknown>);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Item 1d. This page had no way back at all. `/brokers/:id/settings` is
          opened from that broker's detail page, so that is where Back goes;
          a broker admin on `/broker/settings` has no such parent (and no
          `:id`), so they get no button rather than a link to a page they are
          not allowed to open. */}
      {brokerIdParam && (
        <div>
          <BackButton to={`/brokers/${brokerIdParam}`} label="Back to broker" />
        </div>
      )}
      <PageHeader title="Broker Settings" subtitle="Manage your organization settings" />

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="max-w-2xl">
        {activeTab === 'general' && (
          <Card className="p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">General Settings</h3>

            {/* Item 1e: a real upload, not a URL you pasted from somewhere else.
                This reuses the platform's media library (`apps.assets`) through
                the same `AssetPickerField` insurer logos and product icons use
                — upload OR pick something already there, with content-based
                format sniffing, an SVG sanitiser, optimisation and responsive
                variants behind it. What is stored is the asset's `stable_url`,
                the permanent handle that survives the file being replaced with
                a different format; `asset.url` moves and must never be stored.
                Accepted formats and the size cap are read from
                `/assets/config/` rather than restated here, so this cannot
                drift from what the server will actually take. */}
            <div>
              <AssetPickerField
                label="Logo"
                hint={
                  assetConfig
                    ? `PNG, JPEG, WebP, SVG, GIF, BMP, AVIF and ICO (favicons) up to ${assetConfig.max_upload_mb} MB.`
                    : 'Upload an image, or choose one already in the media library.'
                }
                previewClassName="aspect-[3/1]"
                asset={pickedLogo}
                fallbackUrl={!pickedLogo && form.logo_url ? form.logo_url : null}
                onChange={(asset: MediaAsset | null) => {
                  setPickedLogo(asset);
                  // stable_url, never `url` — see the note above.
                  updateField('logo_url', asset ? asset.stable_url : '');
                }}
              />
              {assetConfigError && (
                <p className="mt-1 text-xs text-amber-700">
                  The library&apos;s upload limits could not be read, so the picker may accept a
                  file the server then refuses.
                </p>
              )}
            </div>

            <div className="flex items-center gap-3">
              <input
                id="show-powered-by"
                type="checkbox"
                checked={form.show_powered_by ?? true}
                onChange={(e) => updateField('show_powered_by', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="show-powered-by" className="text-sm text-gray-700 dark:text-gray-300">
                Show &quot;Powered by Policy.online&quot; badge
              </label>
            </div>

            {/* V-01(a). The control is phrased as the CHECK, not as the
                permission, because that is the thing being switched on: the
                default is off, and a broker who wants the old refusal ticks
                this. `allow_duplicate_contacts` stays the stored shape — one
                boolean, inverted here only for the label. */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-start gap-3">
                <input
                  id="strict-duplicate-contacts"
                  type="checkbox"
                  checked={form.allow_duplicate_contacts === false}
                  onChange={(e) => updateField('allow_duplicate_contacts', !e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <label
                    htmlFor="strict-duplicate-contacts"
                    className="text-sm text-gray-700 dark:text-gray-300"
                  >
                    Refuse duplicate contacts
                  </label>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Off by default: the same email or phone may be entered on more than one
                    contact. Tick this to refuse a new contact whose email or phone already
                    belongs to another contact of yours. An email or phone that belongs to a
                    staff, administrator or insurer login is refused either way.
                  </p>
                </div>
              </div>
            </div>

            {/* Public quote page: both choices are stored in `Broker.settings`
                but travel as their own fields, like the duplicate-contacts
                switch, so saving them cannot clobber the rest of that blob. */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">Public quote page</h4>
              <fieldset>
                <legend className="text-sm text-gray-700 dark:text-gray-300">
                  Customer choices (IDV, no-claim bonus, add-ons)
                </legend>
                <div className="mt-2 space-y-2">
                  {(
                    [
                      ['quote_first', 'Quote first, then let the customer customise', 'Prices appear straight away with each insurer’s defaults; the customer can then change IDV, claim / no-claim bonus and add-ons and update the prices.'],
                      ['ask_first', 'Ask before quoting', 'The quote form asks about claims, no-claim bonus, add-ons and IDV before fetching the first prices.'],
                    ] as const
                  ).map(([value, label, hint]) => (
                    <label key={value} className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="radio"
                        name="public-quote-customise"
                        value={value}
                        checked={(form.public_quote_customise ?? 'quote_first') === value}
                        onChange={() => updateField('public_quote_customise', value)}
                        className="mt-0.5 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>
                        {label}
                        <span className="block text-xs text-gray-500 dark:text-gray-400">{hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <fieldset>
                <legend className="text-sm text-gray-700 dark:text-gray-300">When the customer can pay online</legend>
                <div className="mt-2 space-y-2">
                  {(
                    [
                      ['after_approval', 'After I approve their KYC documents'],
                      ['immediate', 'Straight after they choose an option'],
                    ] as const
                  ).map(([value, label]) => (
                    <label key={value} className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="radio"
                        name="online-payment-gate"
                        value={value}
                        checked={(form.online_payment_gate ?? 'after_approval') === value}
                        onChange={() => updateField('online_payment_gate', value)}
                        className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>
          </Card>
        )}

        {activeTab === 'branding' && (
          <Card className="p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Branding</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Primary Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.primary_color || '#1E40AF'}
                    onChange={(e) => updateField('primary_color', e.target.value)}
                    className="h-10 w-10 rounded cursor-pointer border dark:border-gray-600"
                  />
                  <Input
                    value={form.primary_color || ''}
                    onChange={(e) => updateField('primary_color', e.target.value)}
                    placeholder="#1E40AF"
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Secondary Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.secondary_color || '#3B82F6'}
                    onChange={(e) => updateField('secondary_color', e.target.value)}
                    className="h-10 w-10 rounded cursor-pointer border dark:border-gray-600"
                  />
                  <Input
                    value={form.secondary_color || ''}
                    onChange={(e) => updateField('secondary_color', e.target.value)}
                    placeholder="#3B82F6"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            <div className="rounded-lg p-4 border dark:border-gray-700" style={{ backgroundColor: form.primary_color || '#1E40AF' }}>
              <p className="text-white text-sm font-medium">Preview: Primary color</p>
            </div>
          </Card>
        )}

        {activeTab === 'domain' && (
          <Card className="p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Custom Domain</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Configure a custom domain for your white-label portal.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Domain
              </label>
              <Input
                value={form.custom_domain || ''}
                onChange={(e) => updateField('custom_domain', e.target.value)}
                placeholder="insurance.yourcompany.com"
              />
            </div>
            {form.custom_domain && (
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-sm space-y-2">
                <p className="font-medium text-gray-900 dark:text-white">CNAME Setup Instructions:</p>
                <p className="text-gray-600 dark:text-gray-300">
                  Add a CNAME record in your DNS settings:
                </p>
                <code className="block bg-gray-100 dark:bg-gray-800 p-2 rounded text-xs">
                  {form.custom_domain} CNAME portal.basepolicy.online
                </code>
              </div>
            )}
          </Card>
        )}

        {activeTab === 'commission' && (
          <Card className="p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Commission Defaults</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Default tax and payout settings applied to commission payouts.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Default TDS %
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.default_tds_percentage || ''}
                  onChange={(e) => updateField('default_tds_percentage', e.target.value)}
                  placeholder="10.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Default GST %
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.default_gst_percentage || ''}
                  onChange={(e) => updateField('default_gst_percentage', e.target.value)}
                  placeholder="18.00"
                />
              </div>
            </div>
            <SearchableSelect
              label="Payout Frequency"
              value={form.payout_frequency || 'monthly'}
              onChange={(value) => updateField('payout_frequency', value)}
              options={[
                { value: 'monthly', label: 'Monthly' },
                { value: 'quarterly', label: 'Quarterly' },
              ]}
            />
            <div>
              <SearchableSelect
                label="Commission Settlement Policy"
                value={form.settlement_policy || 'pay_expected_then_settle'}
                onChange={(value) => updateField('settlement_policy', value)}
                options={settlementPolicyOptions}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                "Block until reconciled" holds commission-based payouts until their source
                calculations are reconciled.
              </p>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">POSP XP points</h4>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Agents see what a sale earns them as XP points instead of a commission figure, which
                IRDAI does not allow on screen. Customers never see XP.
              </p>
              <div className="mt-3 max-w-xs">
                <Input
                  id="posp-xp-ratio"
                  label="Rupees of agent payout per 1 XP"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.posp_xp_ratio ?? '10.00'}
                  onChange={(e) => updateField('posp_xp_ratio', e.target.value)}
                  helperText="Default 10: a payout of ₹1,200 shows as 120 XP. A change applies to policies credited from now on."
                />
              </div>
            </div>
          </Card>
        )}
        {activeTab === 'messaging' && <MessagingChannelsSection />}
        {activeTab === 'assistants' && <BrokerAssistantKeysSection />}
        {activeTab === 'kyc' && <BrokerKycSection />}
      </div>

      {/* Sticky Save Bar */}
      {isDirty && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 flex items-center justify-end gap-3 shadow-lg z-50">
          <span className="text-sm text-gray-500 dark:text-gray-400">You have unsaved changes</span>
          <Button
            variant="secondary"
            onClick={() => { setForm(settings || {}); setIsDirty(false); }}
          >
            Discard
          </Button>
          <Button onClick={handleSave} disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      )}
    </div>
  );
}
